import { useRef, useState } from 'react';
import { useAchievementTypes, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { Button } from 'components/common';
import {
  AccessAndVisibility,
  AccessAndVisibilityCriteria
} from 'components/common/accessAndVisibility';
import { Box } from 'components/common/box';
import { Checkbox, Form, FormGroup, FormSection } from 'components/common/form';
import { UnsavedFormChanges } from 'components/common/notifications';

import type { FormApi } from 'informed';
import type { Achievement } from 'shared-types';
import type { AccessAndVisibilityFormElements } from 'components/common/accessAndVisibility/AccessAndVisibility';

type FormState = AccessAndVisibilityFormElements<
  'groups' | 'sso' | 'userAttributes'
> &
  Pick<
    Achievement,
    | 'allowClosingConversation'
    | 'allowClosingConversationRequireApproval'
    | 'allowClosingConversationUser'
    | 'allowConfidential'
    | 'allowConfidentialIncludeAttachments'
    | 'allowConfidentialUserCanSet'
    | 'isIncompleteViewableByUser'
  >;

const AchievementAccessAndVisibility = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { achievement }
  } = useReduxState();

  const formApi = useRef<FormApi<FormState>>(undefined);

  const [loading, setLoading] = useState(false);

  const {
    hasTemplate,
    isGroupAchievement,
    supportConfidential,
    supportClosing
  } = useAchievementTypes({ type: achievement?.type });

  const handleSubmit = async (values: FormState) => {
    setLoading(true);

    try {
      const modifiedAchievement = {
        ...achievement,
        ...values,
        ...(!isGroupAchievement
          ? {
              userAccess: (values.visibility === 'private'
                ? 'none'
                : values.visibility) as Achievement['userAccess'],
              userAccessRequireApproval:
                values.restrictions === 'request-access',
              visibilityCriteria: {
                sso: values.visibilityCriteria.sso,
                groups: values.visibilityCriteria.groups,
                userAttributes: values.visibilityCriteria.userAttributes
              }
            }
          : {})
      };

      await api.put(
        `/admin/achievements/${achievement.id}`,
        modifiedAchievement
      );
      dispatch.updatePageState({
        achievement: modifiedAchievement,
        hasUnsavedChanges: false,
        refreshInitialState: true
      });
      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setLoading(false);
    }
  };

  const initialValues: FormState = {
    allowClosingConversation: achievement.allowClosingConversation,
    allowClosingConversationRequireApproval:
      achievement.allowClosingConversationRequireApproval,
    allowClosingConversationUser: achievement.allowClosingConversationUser,
    allowConfidential: achievement.allowConfidential,
    allowConfidentialIncludeAttachments:
      achievement.allowConfidentialIncludeAttachments,
    allowConfidentialUserCanSet: achievement.allowConfidentialUserCanSet,
    isIncompleteViewableByUser: achievement.isIncompleteViewableByUser,
    restrictions: achievement.userAccessRequireApproval
      ? 'request-access'
      : 'none',
    visibility:
      achievement.userAccess === 'none' ? 'private' : achievement.userAccess,
    visibilityCriteria: {
      groups: achievement.visibilityCriteria?.groups || false,
      sso: achievement.visibilityCriteria?.sso || false,
      userAttributes: achievement.visibilityCriteria?.userAttributes || false
    }
  };

  // Clear dependent fields when allowClosingConversation is disabled
  const handleAllowClosingConversationChange = (value: boolean) => {
    if (!value && formApi.current) {
      formApi.current.setValue('allowClosingConversationUser', false);
      formApi.current.setValue(
        'allowClosingConversationRequireApproval',
        false
      );
    }
  };

  // Clear approval requirement when user closing is disabled
  const handleAllowClosingConversationUserChange = (value: boolean) => {
    if (!value && formApi.current) {
      formApi.current.setValue(
        'allowClosingConversationRequireApproval',
        false
      );
    }
  };

  return (
    <>
      <Box padding={0} title={messages.eventCalendarSettingsAccess}>
        <Form
          initialValues={initialValues}
          onSubmit={handleSubmit}
          getApi={(form) => (formApi.current = form)}
        >
          {({ formState }) => (
            <>
              <FormSection maxWidth="100%">
                <UnsavedFormChanges initialValues={initialValues} />

                {!isGroupAchievement ? (
                  <AccessAndVisibility
                    customLabels={{
                      headerUserAccess: messages.achievementAccessAndVisibility,
                      restrictionOptionUserRequest:
                        messages.achievementAccessAndVisibilityRequest,
                      visibilityOptionPrivate:
                        messages.achievementAccessAndVisibilityAdminsOnly,
                      visibilityOptionAnyone:
                        messages.achievementAccessAndVisibilityUsers,
                      visibilityOptionLimited:
                        messages.achievementAccessAndVisibilityUsersLimited
                    }}
                    restrictions={{
                      none: true,
                      'user-limit': false,
                      'request-access': true
                    }}
                    hasPendingRequests={
                      achievement.numberOfPendingAccessRequests > 0
                    }
                  />
                ) : null}

                {hasTemplate ? (
                  <FormGroup label={messages.achievementIncomplete}>
                    <Checkbox
                      disabled={achievement.template?.some(
                        (item) =>
                          item.isMarkableByUser || item.isAnswerableByUser
                      )}
                      field="isIncompleteViewableByUser"
                      id="isIncompleteViewableByUser"
                      label={
                        achievement.type === 'tasklist'
                          ? messages.achievementIncompleteEnableTasks
                          : messages.achievementIncompleteEnableQuestions
                      }
                      labelTooltip={
                        achievement.type === 'tasklist'
                          ? messages.achievementIncompleteEnableInfoTasks
                          : messages.achievementIncompleteEnableInfoQuestions
                      }
                    />
                  </FormGroup>
                ) : null}

                {supportConfidential ? (
                  <FormGroup
                    label={messages.commonConfidential}
                    labelTooltip={
                      messages.achievementAccessAndVisibilityConfidentialInfo
                    }
                  >
                    <Checkbox
                      field="allowConfidential"
                      id="allowConfidential"
                      label={
                        messages.achievementAccessAndVisibilityConfidentialAllowAdmin
                      }
                    />

                    <Checkbox
                      disabled={!formState.values.allowConfidential}
                      field="allowConfidentialUserCanSet"
                      id="allowConfidentialUserCanSet"
                      label={
                        messages.achievementAccessAndVisibilityConfidentialAllowUser
                      }
                      style={{ marginTop: 5 }}
                    />

                    <Checkbox
                      disabled={!formState.values.allowConfidential}
                      field="allowConfidentialIncludeAttachments"
                      id="allowConfidentialIncludeAttachments"
                      label={
                        messages.achievementAccessAndVisibilityConfidentialIncludeAttachments
                      }
                      style={{ marginTop: 5 }}
                    />
                  </FormGroup>
                ) : null}

                {supportClosing ? (
                  <FormGroup
                    label={messages.achievementCloseConversation}
                    labelTooltip={messages.achievementCloseConversationInfo}
                  >
                    <Checkbox
                      field="allowClosingConversation"
                      id="allowClosingConversation"
                      label={messages.achievementCloseConversationAllowAdmin}
                      onValueChange={handleAllowClosingConversationChange}
                    />

                    <Checkbox
                      disabled={!formState.values.allowClosingConversation}
                      field="allowClosingConversationUser"
                      id="allowClosingConversationUser"
                      label={messages.achievementCloseConversationAllowUser}
                      onValueChange={handleAllowClosingConversationUserChange}
                      style={{ marginTop: 5 }}
                    />

                    <Checkbox
                      disabled={
                        !formState.values.allowClosingConversation ||
                        !formState.values.allowClosingConversationUser ||
                        !formState.values.allowConfidential
                      }
                      field="allowClosingConversationRequireApproval"
                      id="allowClosingConversationRequireApproval"
                      label={
                        messages.achievementCloseConversationRequireApproval
                      }
                      style={{ marginTop: 5 }}
                    />
                  </FormGroup>
                ) : null}
              </FormSection>

              <FormSection>
                <Button
                  category="success"
                  disabled={formState.pristine}
                  loading={loading}
                  text={messages.commonSave}
                  type="submit"
                />
              </FormSection>
            </>
          )}
        </Form>
      </Box>

      {!isGroupAchievement ? (
        <div style={{ marginTop: 40 }}>
          <AccessAndVisibilityCriteria
            isAvailable={{ groups: true, userAttributes: true }}
            isEnabled={{
              groups: achievement?.visibilityCriteria?.groups || false,
              userAttributes:
                achievement?.visibilityCriteria?.userAttributes || false
            }}
            productId={achievement.id}
            type="achievement"
          />
        </div>
      ) : null}
    </>
  );
};

export default AchievementAccessAndVisibility;
